import { Auth } from 'aws-amplify';

import analytics from '@/analytics';
import { trimStringAndLowerCase } from '@/assets/utilfunctions';
import { PREVIOUS_AUTH_SYSTEM_KEY } from '@/constants/authKeys';
import { AuthSignInResult, GCAuth, GCAUTH_ACCESS_TOKEN_KEY, GCAUTH_REFRESH_TOKEN_KEY } from '@/services/gcauth.service';
import { getValueFromLocalstore, saveValueToLocalstore } from '@/services/localStorage.service';
import loggerService from '@/services/logger.service';
import { getAuthTimingData } from '@/utils/authPerformance';
import { exponentialRetry } from '@/utils/utils';

const RETRY_DELAY = 3000;
const GCAUTH_MAX_RETRIES = 3;
const COGNITO_MAX_RETRIES = 5;
const SESSION_VALIDATION_INTERVAL = 300000; // 5 minutes
const COGNITO_RATE_LIMIT_ERROR_CODE = 'TooManyRequestsException';

interface AuthSignInParams {
  userEmailOrUserName: string;
  secretCode: string;
  eventId: string;
  shortId: string;
  store: any;
  currentUser: any;
  useGCAuth: boolean;
}

interface AuthSignInResponse {
  user: any;
  emailId: string;
}

interface CheckLinkParams {
  user: any;
  userEmailOrUserName: string;
  secretCode: string;
  eventId: string;
  shortId: string;
  useGCAuth: boolean;
}

interface RetryCallbacks {
  onRetry?: (attempt: number) => void;
}

/**
 * Stores GCAuth tokens in both Vuex store and localStorage
 */
export function storeGCAuthTokens(store: any, accessToken?: string, refreshToken?: string): void {
  if (!accessToken || !refreshToken) {
    loggerService.warn('Warning: Failed to store GCAuth tokens', accessToken, refreshToken);
    return;
  }

  store.commit('setAccessToken', accessToken);
  store.commit('setRefreshToken', refreshToken);

  saveValueToLocalstore(GCAUTH_ACCESS_TOKEN_KEY, accessToken);
  saveValueToLocalstore(GCAUTH_REFRESH_TOKEN_KEY, refreshToken);
  saveValueToLocalstore(PREVIOUS_AUTH_SYSTEM_KEY, 'gcauth');
}

/**
 * Signs in user using username/email for authentication
 */
export async function authSignIn(params: AuthSignInParams, callbacks?: RetryCallbacks): Promise<AuthSignInResponse> {
  const { userEmailOrUserName, secretCode, eventId, shortId, store, currentUser, useGCAuth } = params;

  const executeSignIn = async (): Promise<AuthSignInResponse> => {
    return new Promise((resolve, reject) => {
      if (useGCAuth) {
        GCAuth.signIn(userEmailOrUserName, secretCode, eventId, shortId)
          .then((result: AuthSignInResult) => {
            if (!result.challengeParam) {
              throw new Error('This link is invalid or broken. Please request a new link to continue.');
            }

            const { accessToken, refreshToken } = result.signInUserSession;
            const emailId = result.challengeParam.email;

            if (currentUser && currentUser.email !== emailId) {
              // different user logged in
              resolve({ user: result, emailId });
              return;
            }

            storeGCAuthTokens(store, accessToken.jwtToken, refreshToken.token);
            resolve({ user: result, emailId });
          })
          .catch(err => {
            loggerService.warn('Warning: Failed to Sign in user using GCAuth', userEmailOrUserName, err);
            reject(err instanceof Error ? err : new Error(String(err)));
          });
      } else {
        Auth.signIn(trimStringAndLowerCase(userEmailOrUserName))
          .then(user => {
            const emailId = user?.challengeParam?.email ?? userEmailOrUserName;
            saveValueToLocalstore(PREVIOUS_AUTH_SYSTEM_KEY, 'cognito');
            resolve({ user, emailId });
          })
          .catch(err => {
            loggerService.warn('Warning: Failed to Sign in user using Cognito', userEmailOrUserName, err);
            reject(err instanceof Error ? err : new Error(String(err)));
          });
      }
    });
  };

  return exponentialRetry(executeSignIn, {
    retries: useGCAuth ? GCAUTH_MAX_RETRIES : COGNITO_MAX_RETRIES,
    delayMs: RETRY_DELAY,
    shouldRetry: (e: Error) => {
      if (useGCAuth && (e as any)?.statusCode === 409 && (e as any)?.error === 'session_exists') {
        return false;
      }
      return true;
    },
    onRetry: (e, attempt) => {
      const errorCode = (e as any)?.code;
      if (useGCAuth) {
        loggerService.warn(`GCAuth retry attempt ${attempt} for user ${userEmailOrUserName}.`, e);
      } else if (errorCode === COGNITO_RATE_LIMIT_ERROR_CODE) {
        loggerService.warn(`Cognito rate limit - retry attempt ${attempt} for user ${userEmailOrUserName}.`);
      }
      callbacks?.onRetry?.(attempt);
    },
    onMaxReached: e => {
      loggerService.error(`Max retries reached for sign in user ${userEmailOrUserName}.`, e);
    }
  });
}

/**
 * Verifies the magic link and completes Cognito authentication
 * Only used for Cognito authentication
 */
export async function checkMagicLink(params: CheckLinkParams, callbacks?: RetryCallbacks): Promise<void> {
  const { user, userEmailOrUserName, secretCode, eventId, shortId, useGCAuth } = params;

  if (useGCAuth) {
    return;
  }

  const executeCheckLink = async () => {
    let currentUser = user;

    if (!currentUser) {
      loggerService.info('Signing in with email because user is not found', userEmailOrUserName);
      currentUser = await Auth.signIn(trimStringAndLowerCase(userEmailOrUserName));
    }

    const cognitoUser = await Auth.sendCustomChallengeAnswer(currentUser, secretCode, {
      shortId,
      app: 'event',
      eventId
    });
    if (!cognitoUser.signInUserSession) {
      throw new Error('This link is invalid or broken. Please request a new link to continue.');
    }
  };

  return exponentialRetry(executeCheckLink, {
    retries: COGNITO_MAX_RETRIES,
    delayMs: RETRY_DELAY,
    onRetry: (e, attempt) => {
      const errorCode = (e as any)?.code;
      if (errorCode === COGNITO_RATE_LIMIT_ERROR_CODE) {
        loggerService.warn(`Cognito rate limit - retry attempt ${attempt} for checkLink.`);
      }
      callbacks?.onRetry?.(attempt);
    },
    onMaxReached: e => {
      loggerService.error('Max retries reached for checkLink.', e);
    }
  });
}

/**
 * Tracks magic link verification in analytics with performance timing data.
 * Timing data and auth context are retrieved from the authPerformance module.
 */
export function trackMagicLinkVerification(params: {
  email: string;
  eventId: string;
  secretCode: string;
  userId: string;
  isEmbedded?: boolean;
}): void {
  const { email, eventId, secretCode, userId, isEmbedded = false } = params;

  const timing = getAuthTimingData();

  analytics.track(isEmbedded ? 'embedded_magic_link_verified' : 'magic_link_verified', {
    email,
    event_id: eventId,
    magic_link_hash: secretCode,
    user_id: userId,
    perf_signin_ms: timing.signInDurationMs,
    perf_check_link_ms: timing.checkLinkDurationMs,
    perf_fetch_user_ms: timing.fetchUserDurationMs,
    perf_total_auth_ms: timing.totalAuthDurationMs,
    perf_retry_count: timing.retryCount,
    perf_auth_system: timing.authSystem
  });

  loggerService.track('auth_system_login', {
    auth_system: timing.authSystem,
    event_id: eventId,
    user_id: userId,
    retry_count: timing.retryCount
  });
}

/**
 * Constants for magic link authentication
 */
export const MAGIC_LINK_CONSTANTS = {
  RETRY_DELAY,
  GCAUTH_MAX_RETRIES,
  COGNITO_MAX_RETRIES,
  COGNITO_RATE_LIMIT_ERROR_CODE
};

// Session validation polling
let intervalId: ReturnType<typeof setInterval> | null = null;

/**
 * Starts polling to validate the session token periodically.
 * Calls onInvalid callback when session becomes invalid (401/403).
 */
export function startSessionValidationPolling(onInvalid: () => void): void {
  stopSessionValidationPolling();

  intervalId = setInterval(async () => {
    const token = getValueFromLocalstore(GCAUTH_ACCESS_TOKEN_KEY);
    if (!token) {
      stopSessionValidationPolling();
      onInvalid();
      return;
    }

    try {
      const response = await GCAuth.validateGCAuthToken(token);
      if (response?.error === 'token_revoked') {
        stopSessionValidationPolling();
        onInvalid();
      }
    } catch (err) {
      loggerService.warn('[GCAuth] Session validation poll failed', err);
    }
  }, SESSION_VALIDATION_INTERVAL);
}

/**
 * Stops the session validation polling.
 */
export function stopSessionValidationPolling(): void {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
}
